import type { Empresa } from '@/types/empresa'
import type { DetalheFatura, Fatura } from '@/types/fatura'
import { parseDateInput } from '@/utils'
import api from './api'

export type MetodoPagamento = 'pix' | 'boleto' | 'cartao' | 'bank_deposit'

export interface TaxasAcrescimo {
    multa: number
    juros: number
    repasse: number
    tarifa: number
    taxa: number
}

export async function getFaturas(month: number | string, year: number | string, clienteHash?: string): Promise<Fatura[]> {
    const resp = await api.get(`/clientes/faturas?month=${month}&year=${year}`, {
        headers: { clienteHash },
    })
    return resp.data
}

export interface PagedFaturasResponse<T> {
    current_page: number
    data: T[]
    from: number | null
    last_page: number
    per_page: number
    to: number | null
    total: number
}

export interface FaturasAbertasResponse {
    faturas: Fatura[]
    dataLimite?: string
}

export async function getFaturasAbertas(dataLimite?: string, clienteHash?: string): Promise<FaturasAbertasResponse> {
    const resp = await api.get('/clientes/faturas', {
        params: {
            status: 'open',
            dataLimite,
        },
        headers: { clienteHash },
    })

    return {
        faturas: resp.data,
        dataLimite: (resp.headers['x-data-limite'] as string | undefined) ?? dataLimite,
    }
}

export async function getFaturasPagas(page = 1, perPage = 12, clienteHash?: string): Promise<PagedFaturasResponse<Fatura>> {
    const resp = await api.get('/clientes/faturas', {
        params: {
            status: 'paid',
            page,
            perPage,
        },
        headers: { clienteHash },
    })

    return resp.data
}

export const isFaturaAberta = (fatura: Fatura): boolean =>
    !fatura.Pago && !fatura.boleto && !fatura.pix

export const sortByExpBoleto = (a: Fatura, b: Fatura): number =>
    (parseDateInput(a.boleto!.Vencimento)?.getTime() ?? 0) > (parseDateInput(b.boleto!.Vencimento)?.getTime() ?? 0)
        ? -1
        : 1

export const sortByExpiration = (a: Fatura, b: Fatura): number =>
    (parseDateInput(a.DataVencimento)?.getTime() ?? 0) > (parseDateInput(b.DataVencimento)?.getTime() ?? 0)
        ? 1
        : -1

export const sortByPayment = (a: Fatura, b: Fatura): number =>
    (parseDateInput(a.DataPagamento!)?.getTime() ?? 0) > (parseDateInput(b.DataPagamento!)?.getTime() ?? 0)
        ? -1
        : 1

export function getDescontoAntecipadoAplicavel(fatura: Fatura, referenceDate = new Date()): number {
    if (fatura.Pago) return 0

    const desconto = Number(fatura.DescontoAntecipacao ?? 0)
    if (desconto <= 0) return 0

    const vencimento = parseDateInput(fatura.DataVencimento)
    if (!vencimento) return 0

    const hoje = new Date(referenceDate)
    hoje.setHours(0, 0, 0, 0)

    const vencimentoNormalizado = new Date(vencimento)
    vencimentoNormalizado.setHours(0, 0, 0, 0)

    return hoje.getTime() <= vencimentoNormalizado.getTime() ? desconto : 0
}

export function getValorComDescontoAntecipado(fatura: Fatura, referenceDate = new Date()): number {
    return Number(fatura.Valor) - getDescontoAntecipadoAplicavel(fatura, referenceDate)
}

export function formaPagamentoPadrao(empresa?: Empresa | null): MetodoPagamento | '' {
    if (!empresa) return ''
    if (empresa.CdFormaPix > 0) return 'pix'
    if (empresa.CdFormaBoleto > 0) return 'boleto'
    if (empresa.CdFormaCartao > 0) return 'cartao'
    if (empresa.PermiteDeposito) return 'bank_deposit'
    return ''
}

export function getTaxasMetodoPagamento(
    empresa?: Empresa | null,
    metodo?: string,
): TaxasAcrescimo {
    const taxas = empresa?.TaxasPagamento
    const vazio = { multa: 0, juros: 0, repasse: 0, tarifa: 0, taxa: 0 }

    if (!taxas) return vazio

    const mapear = (forma?: { Multa?: number; Juros?: number; Repasse?: number; Tarifa?: number; Taxa?: number }) => ({
        multa: Number(forma?.Multa ?? 0),
        juros: Number(forma?.Juros ?? 0),
        repasse: Number(forma?.Repasse ?? 0),
        tarifa: Number(forma?.Tarifa ?? 0),
        taxa: Number(forma?.Taxa ?? 0),
    })

    const porMetodo: Record<string, TaxasAcrescimo> = {
        pix: mapear(taxas.pix),
        boleto: mapear(taxas.boleto),
        cartao: mapear(taxas.cartao),
    }

    if (metodo && porMetodo[metodo]) return porMetodo[metodo]

    const padrao = formaPagamentoPadrao(empresa)
    if (padrao && porMetodo[padrao]) return porMetodo[padrao]

    return vazio
}

/**
 * Mesma regra de salvaformapagamento.php?acao=veracrescimo
 */
export function getAcrescimoRepasseTaxas(
    valorBase: number,
    taxas: TaxasAcrescimo,
): number {
    if (taxas.repasse !== 1 || valorBase <= 0) return 0

    let valorComTaxa = 0
    if (taxas.taxa > 0) {
        valorComTaxa = Math.round((valorBase / (1 - taxas.taxa / 100)) * 100) / 100
    }

    let acrescimo = valorBase > valorComTaxa ? 0 : valorComTaxa - valorBase
    acrescimo += Number(taxas.tarifa ?? 0)

    return Math.round(acrescimo * 100) / 100
}

function startOfDay(date: Date): Date {
    const normalized = new Date(date)
    normalized.setHours(0, 0, 0, 0)
    return normalized
}

export function getDiasAtrasoFatura(fatura: Fatura, referenceDate = new Date()): number {
    if (fatura.Pago || fatura.boleto || fatura.pix) return 0

    const vencimento = parseDateInput(fatura.DataVencimento)
    if (!vencimento) return 0

    const diffDays = Math.round(
        (startOfDay(referenceDate).getTime() - startOfDay(vencimento).getTime()) / 86400000,
    )

    return diffDays > 0 ? diffDays : 0
}

export function getMultaAplicavel(
    fatura: Fatura,
    taxas: TaxasAcrescimo,
    referenceDate = new Date(),
): number {
    if (getDiasAtrasoFatura(fatura, referenceDate) <= 0 || taxas.multa <= 0) return 0
    return Number(fatura.Valor) * (taxas.multa / 100)
}

export function getJurosAplicavel(
    fatura: Fatura,
    taxas: TaxasAcrescimo,
    referenceDate = new Date(),
): number {
    const diasAtraso = getDiasAtrasoFatura(fatura, referenceDate)
    if (diasAtraso <= 0 || taxas.juros <= 0) return 0
    return Number(fatura.Valor) * (taxas.juros / 100) * diasAtraso
}

export function getTotalBrutoFaturas(faturas: Fatura[]): number {
    return faturas.reduce((sum, fatura) => sum + Number(fatura.Valor), 0)
}

export function getTotalDescontoAntecipadoFaturas(faturas: Fatura[], referenceDate = new Date()): number {
    return faturas.reduce((sum, fatura) => sum + getDescontoAntecipadoAplicavel(fatura, referenceDate), 0)
}

export function getTotalMultaFaturas(
    faturas: Fatura[],
    taxas: TaxasAcrescimo,
    referenceDate = new Date(),
): number {
    return faturas.reduce((sum, fatura) => sum + getMultaAplicavel(fatura, taxas, referenceDate), 0)
}

export function getTotalJurosFaturas(
    faturas: Fatura[],
    taxas: TaxasAcrescimo,
    referenceDate = new Date(),
): number {
    return faturas.reduce((sum, fatura) => sum + getJurosAplicavel(fatura, taxas, referenceDate), 0)
}

export function getTotalLiquidoFaturas(
    faturas: Fatura[],
    referenceDate = new Date(),
    taxas: TaxasAcrescimo = { multa: 0, juros: 0, repasse: 0, tarifa: 0, taxa: 0 },
): number {
    const subtotal =
        getTotalBrutoFaturas(faturas)
        - getTotalDescontoAntecipadoFaturas(faturas, referenceDate)
        + getTotalMultaFaturas(faturas, taxas, referenceDate)
        + getTotalJurosFaturas(faturas, taxas, referenceDate)

    return subtotal + getAcrescimoRepasseTaxas(subtotal, taxas)
}

export function groupByBoleto(faturas: Fatura[]): Fatura[][] {
    const group: Record<number, Fatura[]> = {}
    for (const fatura of faturas) {
        const CdBoleto = fatura.boleto?.CdBoleto
        if (!CdBoleto) continue

        group[CdBoleto] = group[CdBoleto] ?? []
        group[CdBoleto].push(fatura)
    }
    return Object.values(group).sort((a, b) => sortByExpBoleto(a[0], b[0]))
}

export function groupByPix(faturas: Fatura[]): Fatura[][] {
    const group: Record<number, Fatura[]> = {}
    for (const fatura of faturas) {
        const CdPix = fatura.pix?.CdPix
        if (!CdPix) continue

        group[CdPix] = group[CdPix] ?? []
        group[CdPix].push(fatura)
    }
    return Object.values(group)
}

/** Agrupa faturas pelo pagamento que as quitou (PagamentoFatura). */
export function groupByPagamento(faturas: Fatura[]): Fatura[][] {
    const group = new Map<string, Fatura[]>()

    for (const fatura of faturas) {
        const key = fatura.pagamento?.CdPagamento
            ? `pgto-${fatura.pagamento.CdPagamento}`
            : `fatura-${fatura.CdFatura}`

        const list = group.get(key) ?? []
        list.push(fatura)
        group.set(key, list)
    }

    return [...group.values()].sort((a, b) => {
        const dateA = a[0].pagamento?.Data ?? a[0].DataPagamento ?? ''
        const dateB = b[0].pagamento?.Data ?? b[0].DataPagamento ?? ''
        if (dateA === dateB) {
            return (b[0].pagamento?.CdPagamento ?? b[0].CdFatura)
                - (a[0].pagamento?.CdPagamento ?? a[0].CdFatura)
        }
        return dateA > dateB ? -1 : 1
    })
}

const TIPO_SERVICO_LABEL: Record<number, string> = {
    3: 'Hospedagem',
    4: 'Creche',
    5: 'Banho',
    7: 'Exame',
    9: 'Atendimento Veterinário',
}

export function getTipoServicoLabel(tipoServico?: number | null): string {
    if (tipoServico == null) return ''
    return TIPO_SERVICO_LABEL[tipoServico] ?? ''
}

/** Categoria no formato da gestão: "Banho, Obama ➜ 18/09/2026" ou "DayUse, NALA" */
export function formatDetalheCategoria(
    detalhe: Pick<DetalheFatura, 'Tipo' | 'TipoServico' | 'Animais' | 'Data' | 'Descricao' | 'Frequencia'>,
    dateText?: string,
): string {
    if (!detalhe.Animais) return ''

    // Fatura de plano: "DayUse, NALA"
    if (detalhe.Tipo === 2) {
        return [detalhe.Descricao, detalhe.Animais].filter(Boolean).join(', ')
    }

    const tipoLabel = getTipoServicoLabel(detalhe.TipoServico)
    // Evita "Banho, Obama" + título "Banho" quando o tipo e a descrição são iguais.
    const prefix =
        tipoLabel && tipoLabel.toLowerCase() !== (detalhe.Descricao ?? '').toLowerCase()
            ? `${tipoLabel}, ${detalhe.Animais}`
            : detalhe.Animais

    return dateText ? `${prefix} ➜ ${dateText}` : prefix
}

export function formatDetalheValor(
    detalhe: Pick<DetalheFatura, 'Plano' | 'ValorBase'>,
    formatMoneyFn: (value: string | number) => string,
): string {
    if (detalhe.Plano) return detalhe.Plano
    if (detalhe.ValorBase == null) return ''

    const valor = Number(detalhe.ValorBase)
    if (!Number.isFinite(valor) || valor === 0) return ''

    if (valor < 0) return `- ${formatMoneyFn(Math.abs(valor))}`
    return formatMoneyFn(valor)
}

export function isDetalheNegativo(detalhe: Pick<DetalheFatura, 'ValorBase'>): boolean {
    return detalhe.ValorBase != null && Number(detalhe.ValorBase) < 0
}

export function groupDetalhesAtendimento(detalhes: DetalheFatura[]): {
    grupos: Array<{ index: number; itens: DetalheFatura[]; principal: DetalheFatura }>
    outros: DetalheFatura[]
} {
    const byIndex = new Map<number, DetalheFatura[]>()
    const outros: DetalheFatura[] = []

    for (const detalhe of detalhes) {
        const index = detalhe.Index ?? detalhe.CdClientePlano
        if ((detalhe.Tipo === 1 || detalhe.Tipo === 2) && index) {
            const itens = byIndex.get(index) ?? []
            itens.push(detalhe)
            byIndex.set(index, itens)
            continue
        }
        outros.push(detalhe)
    }

    const grupos = [...byIndex.entries()].map(([index, itens]) => ({
        index,
        itens,
        principal: itens.find(item => item.Principal) ?? itens[0],
    }))

    return { grupos, outros }
}
